/**
 * MineRadio 用户元数据备份格式。
 *
 * 该模块故意不依赖 main.ts：浏览器 UI 可以直接把解析后的结果合并到
 * SavedState，Node 测试也可以在没有 DOM/Audio/IndexedDB 的环境中验证边界。
 * 备份保存索引、用户元数据与受限的本地歌曲/自建歌单方形封面，不保存音频 Blob、远程封面 URL、临时流 URL 或凭据。
 */
import { assertPlaylistCover, assertPlaylistCoverBudget, playlistCoverFields } from './playlist-cover.ts';

export const METADATA_BACKUP_VERSION = 1 as const;

const MAX_BACKUP_BYTES = 5 * 1024 * 1024;
const MAX_TRACKS = 10_000;
const MAX_QUEUE_ITEMS = 10_000;
const MAX_HISTORY_ITEMS = 1_000;
const MAX_FAVORITES = 10_000;
const MAX_PLAYLISTS = 1_000;
const MAX_PLAYLIST_TRACKS = 10_000;
const MAX_LYRIC_LINES = 20_000;
const MAX_LYRIC_WORDS = 100_000;
const MAX_ARTISTS = 32;
const MAX_METADATA_KEYS = 128;
const MAX_STRING_LENGTH = 8_192;
const MAX_EMBEDDED_LYRIC_TEXT = 1024 * 1024;
const EMBEDDED_LYRIC_FIELDS = new Set(['embeddedPlainLyrics', 'embeddedTranslation', 'embeddedRoman']);
const MAX_REFERENCE_LENGTH = 4_096;
const MAX_POSITION_SECONDS = 24 * 60 * 60;
const MAX_DEPTH = 24;

const DANGEROUS_KEYS = new Set(['__proto__', 'prototype', 'constructor']);
const SECRET_KEY = /(?:password|passwd|token|secret|credential|authorization|cookie|private.?key|api.?key|access.?key|artwork|cover|(?:^|_)url$)/i;
const TEMPORARY_SCHEME = /^(?:blob|data|javascript|file):/i;
const TEMPORARY_QUERY = /(?:^|[?&])(?:token|access_token|refresh_token|sig|signature|expires|expiry|auth|authorization|password|secret|key)=/i;

export type BackupSourceType = 'local' | 'webdav' | 'online';
export type BackupTheme = 'light' | 'dark' | 'system';
export type BackupPlaybackMode = 'sequence' | 'repeat-all' | 'repeat-one' | 'shuffle';
export type BackupMotionLevel = 'high' | 'medium' | 'low' | 'off';

export type MetadataBackupLyricLine = { time: number; text: string };
export type MetadataBackupLyricWord = { startTime: number; endTime: number; word: string };
export type MetadataBackupAmllLine = {
  startTime: number;
  endTime: number;
  words: MetadataBackupLyricWord[];
  translatedLyric: string;
  romanLyric: string;
  isBG: boolean;
  isDuet: boolean;
};

type BackupMetadataValue = string | number | boolean | null | string[] | { ownerId: string; cloudSongId: string; officialSongId: string | null; accountOnly: boolean };
export type MetadataBackupTrack = {
  id: string;
  sourceType: BackupSourceType;
  sourceId: string;
  externalId: string;
  title: string;
  artists: string[];
  album: string;
  durationMs?: number;
  availability: 'unavailable' | 'remote';
  fileName?: string;
  fingerprint?: string;
  size?: number;
  customCover?: string;
  metadata?: Record<string, BackupMetadataValue>;
  lyrics?: MetadataBackupLyricLine[];
  amllLyrics?: MetadataBackupAmllLine[];
  createdAt: string;
  updatedAt: string;
};

export type MetadataBackupPlaylist = {
  id: string;
  name: string;
  description?: string;
  trackIds: string[];
  createdAt: string;
  updatedAt?: string;
  customCover?: string;
  coverUpdatedAt?: string;
};

export type MetadataBackup = {
  version: typeof METADATA_BACKUP_VERSION;
  exportedAt?: string;
  tracks: MetadataBackupTrack[];
  queue: string[];
  history: string[];
  historyPlayedAt: Record<string, number>;
  favorites: string[];
  playlists: MetadataBackupPlaylist[];
  currentId: string | null;
  positions: Record<string, number>;
  lyrics: Record<string, MetadataBackupLyricLine[]>;
  amllLyrics: Record<string, MetadataBackupAmllLine[]>;
  theme: BackupTheme;
  playMode: BackupPlaybackMode;
  lyricOffsetMs: number;
  motionLevel: BackupMotionLevel;
  volume: number;
};

export type MetadataBackupInput = Partial<MetadataBackup> & {
  tracks?: unknown;
  [key: string]: unknown;
};

export class MetadataBackupError extends Error {
  readonly code: string;
  readonly path?: string;

  constructor(code: string, message: string, path?: string) {
    super(message);
    this.name = 'MetadataBackupError';
    this.code = code;
    this.path = path;
  }
}

const fail = (code: string, message: string, path?: string): never => {
  throw new MetadataBackupError(code, message, path);
};

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const own = (value: Record<string, unknown>, key: string): unknown => (
  Object.prototype.hasOwnProperty.call(value, key) ? value[key] : undefined
);

const asString = (value: unknown, path: string, required = false, max = MAX_STRING_LENGTH): string | undefined => {
  if (value === undefined || value === null) {
    if (required) fail('INVALID_SHAPE', `${path} 必须是字符串`, path);
    return undefined;
  }
  if (typeof value !== 'string') fail('INVALID_SHAPE', `${path} 必须是字符串`, path);
  const text = (value as string).trim();
  if (required && !text) fail('INVALID_SHAPE', `${path} 不能为空`, path);
  if (text.length > max) fail('LIMIT_EXCEEDED', `${path} 超出长度限制`, path);
  if (/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/.test(text)) fail('INVALID_VALUE', `${path} 含控制字符`, path);
  return text;
};

const asNumber = (value: unknown, path: string, options: { min?: number; max?: number; integer?: boolean } = {}): number => {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail('INVALID_SHAPE', `${path} 必须是有限数字`, path);
  const number = value as number;
  if (options.integer && !Number.isInteger(number)) fail('INVALID_VALUE', `${path} 必须是整数`, path);
  if (options.min !== undefined && number < options.min) fail('INVALID_VALUE', `${path} 小于允许范围`, path);
  if (options.max !== undefined && number > options.max) fail('INVALID_VALUE', `${path} 超出允许范围`, path);
  return number;
};

const asOptionalNumber = (value: unknown, path: string, options: { min?: number; max?: number } = {}): number | undefined => {
  if (value === undefined || value === null) return undefined;
  return asNumber(value, path, options);
};

const asId = (value: unknown, path: string): string => {
  const id = asString(value, path, true, 256)!;
  if (TEMPORARY_SCHEME.test(id) || /[?#]/.test(id) || TEMPORARY_QUERY.test(id)) fail('INVALID_REFERENCE', `${path} 不能包含临时 URL 或凭据参数`, path);
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]*$/.test(id)) fail('INVALID_REFERENCE', `${path} 不是安全的稳定标识`, path);
  return id;
};

/** Reject temporary stream/blob URLs while retaining a WebDAV path or stable source id. */
const asReference = (value: unknown, path: string): string => {
  const reference = asString(value, path, true, MAX_REFERENCE_LENGTH)!;
  if (TEMPORARY_SCHEME.test(reference) || TEMPORARY_QUERY.test(reference)) fail('INVALID_REFERENCE', `${path} 不能包含临时 URL 或凭据参数`, path);
  try {
    const parsed = new URL(reference);
    if (!/^https?:$/.test(parsed.protocol) || parsed.username || parsed.password || parsed.search || parsed.hash) {
      fail('INVALID_REFERENCE', `${path} 不是可持久化的来源引用`, path);
    }
  } catch {
    // Relative WebDAV hrefs and local relative paths are valid references.
  }
  return reference;
};

const normalizeDate = (value: unknown, path: string): string => {
  if (value === undefined || value === null || value === '') return new Date(0).toISOString();
  const text = asString(value, path, true, 128)!;
  const timestamp = Date.parse(text);
  if (!Number.isFinite(timestamp)) fail('INVALID_VALUE', `${path} 不是有效日期`, path);
  return new Date(timestamp).toISOString();
};

const normalizeVersion = (value: unknown): typeof METADATA_BACKUP_VERSION => {
  if (value === undefined || value === null) return METADATA_BACKUP_VERSION;
  if (value !== METADATA_BACKUP_VERSION) fail('UNSUPPORTED_VERSION', `只支持备份版本 ${METADATA_BACKUP_VERSION}`);
  return METADATA_BACKUP_VERSION;
};

const normalizeTextList = (value: unknown, path: string, maxItems: number, defaultValue: string[] = []): string[] => {
  if (value === undefined || value === null) return [...defaultValue];
  const items: unknown[] = Array.isArray(value) ? value : fail('INVALID_SHAPE', `${path} 必须是数组`, path);
  if (items.length > maxItems) fail('LIMIT_EXCEEDED', `${path} 条目过多`, path);
  const result: string[] = [];
  const seen = new Set<string>();
  items.forEach((item, index) => {
    const id = asId(item, `${path}[${index}]`);
    if (seen.has(id)) fail('DUPLICATE_ID', `${path} 包含重复引用 ${id}`, path);
    seen.add(id);
    result.push(id);
  });
  return result;
};

const normalizeLyricLines = (value: unknown, path: string): MetadataBackupLyricLine[] | undefined => {
  if (value === undefined || value === null) return undefined;
  const items: unknown[] = Array.isArray(value) ? value : fail('INVALID_SHAPE', `${path} 必须是数组`, path);
  if (items.length > MAX_LYRIC_LINES) fail('LIMIT_EXCEEDED', `${path} 行数过多`, path);
  const result = items.map((rawItem, index) => {
    const item = isRecord(rawItem) ? rawItem : fail('INVALID_SHAPE', `${path}[${index}] 必须是对象`, path);
    const timeValue = own(item, 'time') ?? own(item, 'startTime');
    const time = asNumber(timeValue, `${path}[${index}].time`, { min: 0, max: MAX_POSITION_SECONDS * 1000 });
    const text = asString(own(item, 'text') ?? own(item, 'word'), `${path}[${index}].text`, true)!;
    return { time, text };
  });
  result.sort((left, right) => left.time - right.time);
  return result;
};

const normalizeAmllLines = (value: unknown, path: string): MetadataBackupAmllLine[] | undefined => {
  if (value === undefined || value === null) return undefined;
  const items = Array.isArray(value) ? value : fail('INVALID_SHAPE', `${path} 必须是数组`, path);
  if (items.length > MAX_LYRIC_LINES) fail('LIMIT_EXCEEDED', `${path} 行数过多`, path);
  let wordsSeen = 0;
  const result = items.map((rawItem, index) => {
    const item = isRecord(rawItem) ? rawItem : fail('INVALID_SHAPE', `${path}[${index}] 必须是对象`, path);
    const startTime = asNumber(own(item, 'startTime'), `${path}[${index}].startTime`, { min: 0, max: MAX_POSITION_SECONDS * 1000 });
    const endTime = asNumber(own(item, 'endTime'), `${path}[${index}].endTime`, { min: startTime, max: MAX_POSITION_SECONDS * 1000 });
    const rawWords = own(item, 'words');
    const wordItems = rawWords === undefined ? [] : Array.isArray(rawWords) ? rawWords : fail('INVALID_SHAPE', `${path}[${index}].words 必须是数组`, path);
    const words = wordItems.map((rawWord, wordIndex) => {
      const wordRecord = isRecord(rawWord) ? rawWord : fail('INVALID_SHAPE', `${path}[${index}].words[${wordIndex}] 必须是对象`, path);
      const wordStart = asNumber(own(wordRecord, 'startTime'), `${path}[${index}].words[${wordIndex}].startTime`, { min: 0, max: MAX_POSITION_SECONDS * 1000 });
      const wordEnd = asNumber(own(wordRecord, 'endTime'), `${path}[${index}].words[${wordIndex}].endTime`, { min: wordStart, max: MAX_POSITION_SECONDS * 1000 });
      const word = asString(own(wordRecord, 'word'), `${path}[${index}].words[${wordIndex}].word`, false, MAX_STRING_LENGTH) || '';
      wordsSeen += 1;
      if (wordsSeen > MAX_LYRIC_WORDS) fail('LIMIT_EXCEEDED', `${path} 单词数过多`, path);
      return { startTime: wordStart, endTime: wordEnd, word };
    });
    const translatedLyric = asString(own(item, 'translatedLyric'), `${path}[${index}].translatedLyric`);
    const romanLyric = asString(own(item, 'romanLyric'), `${path}[${index}].romanLyric`);
    return {
      startTime,
      endTime,
      words,
      translatedLyric: translatedLyric ?? '',
      romanLyric: romanLyric ?? '',
      isBG: typeof own(item, 'isBG') === 'boolean' ? own(item, 'isBG') as boolean : false,
      isDuet: typeof own(item, 'isDuet') === 'boolean' ? own(item, 'isDuet') as boolean : false,
    };
  });
  result.sort((left, right) => left.startTime - right.startTime);
  return result;
};

const normalizeMetadata = (value: unknown, path: string): Record<string, BackupMetadataValue> | undefined => {
  if (value === undefined || value === null) return undefined;
  const record = isRecord(value) ? value : fail('INVALID_SHAPE', `${path} 必须是对象`, path);
  const keys = Object.keys(record).filter((key) => !DANGEROUS_KEYS.has(key));
  if (keys.length > MAX_METADATA_KEYS) fail('LIMIT_EXCEEDED', `${path} 字段过多`, path);
  const result: Record<string, BackupMetadataValue> = {};
  for (const key of keys) {
    if (SECRET_KEY.test(key)) continue;
    const item = own(record, key);
    if (key === 'artistIds' && Array.isArray(item) && item.length <= 128) result[key] = item.map((id, index) => asString(id, `${path}.${key}[${index}]`, true, 200)!);
    else if (key === 'neteaseCloud' && isRecord(item)) {
      const allowed = ['ownerId', 'cloudSongId', 'officialSongId', 'accountOnly'];
      if (Object.keys(item).some(name => !allowed.includes(name)) || typeof item.accountOnly !== 'boolean') fail('INVALID_SHAPE', `${path}.${key} 云盘身份无效`, `${path}.${key}`);
      const id = (name: string) => {
        const value = own(item, name);
        if (typeof value !== 'string' || !/^\d{1,32}$/.test(value)) fail('INVALID_VALUE', `${path}.${key}.${name} 无效`, `${path}.${key}.${name}`);
        return value as string;
      };
      result[key] = { ownerId: id('ownerId'), cloudSongId: id('cloudSongId'), officialSongId: item.officialSongId === null ? null : id('officialSongId'), accountOnly: item.accountOnly as boolean };
    }
    else if (item === null || typeof item === 'boolean') result[key] = item;
    else if (typeof item === 'number' && Number.isFinite(item)) result[key] = item;
    else if (typeof item === 'string') {
      if (TEMPORARY_SCHEME.test(item) || TEMPORARY_QUERY.test(item)) continue;
      result[key] = asString(item, `${path}.${key}`, false, EMBEDDED_LYRIC_FIELDS.has(key) ? MAX_EMBEDDED_LYRIC_TEXT : MAX_STRING_LENGTH)! || '';
    } else fail('INVALID_SHAPE', `${path}.${key} 只能是基础值`, `${path}.${key}`);
  }
  return Object.keys(result).length ? result : undefined;
};

const normalizeTrack = (value: unknown, index: number): MetadataBackupTrack => {
  const record = isRecord(value) ? value : fail('INVALID_SHAPE', `tracks[${index}] 必须是对象`, `tracks[${index}]`);
  const path = `tracks[${index}]`;
  const sourceTypeValue = own(record, 'sourceType');
  if (sourceTypeValue !== 'local' && sourceTypeValue !== 'webdav' && sourceTypeValue !== 'online') fail('INVALID_VALUE', `${path}.sourceType 无效`, `${path}.sourceType`);
  const sourceType = sourceTypeValue as BackupSourceType;
  const title = asString(own(record, 'title'), `${path}.title`, false, 1_024) || '未命名歌曲';
  const artistsValue = own(record, 'artists');
  let artists: string[];
  if (artistsValue === undefined || artistsValue === null) artists = ['未知艺术家'];
  else {
    const artistItems: unknown[] = Array.isArray(artistsValue) ? artistsValue : fail('INVALID_SHAPE', `${path}.artists 必须是有限数组`, `${path}.artists`);
    if (artistItems.length > MAX_ARTISTS) fail('LIMIT_EXCEEDED', `${path}.artists 条目过多`, `${path}.artists`);
    artists = artistItems.map((artist, artistIndex) => asString(artist, `${path}.artists[${artistIndex}]`, true, 512)!);
    if (!artists.length) artists = ['未知艺术家'];
  }
  const lyrics = normalizeLyricLines(own(record, 'lyrics'), `${path}.lyrics`);
  const amllLyrics = normalizeAmllLines(own(record, 'amllLyrics'), `${path}.amllLyrics`);
  const durationMs = asOptionalNumber(own(record, 'durationMs'), `${path}.durationMs`, { min: 0, max: MAX_POSITION_SECONDS * 1000 });
  const size = asOptionalNumber(own(record, 'size'), `${path}.size`, { min: 0, max: Number.MAX_SAFE_INTEGER });
  const fileName = asString(own(record, 'fileName'), `${path}.fileName`, false, MAX_REFERENCE_LENGTH);
  // importFingerprint uses NUL separators between path, size and duration.
  // This opaque field is never rendered as HTML or used as a URL.
  const rawFingerprint = own(record, 'fingerprint');
  const fingerprint = rawFingerprint === undefined || rawFingerprint === null ? undefined
    : typeof rawFingerprint === 'string' && rawFingerprint.length <= MAX_REFERENCE_LENGTH && !/[\u0001-\u001f\u007f]/.test(rawFingerprint)
      ? rawFingerprint : fail('INVALID_VALUE', `${path}.fingerprint 无效`, path);
  const metadata = normalizeMetadata(own(record, 'metadata'), `${path}.metadata`);
  const track: MetadataBackupTrack = {
    id: asId(own(record, 'id'), `${path}.id`),
    sourceType,
    sourceId: asId(own(record, 'sourceId'), `${path}.sourceId`),
    externalId: asReference(own(record, 'externalId'), `${path}.externalId`),
    title,
    artists,
    album: asString(own(record, 'album'), `${path}.album`, false, 1_024) || '未知专辑',
    availability: sourceType === 'webdav' ? 'remote' : 'unavailable',
    ...(durationMs === undefined ? {} : { durationMs }),
    ...(fileName === undefined ? {} : { fileName }),
    ...(fingerprint === undefined ? {} : { fingerprint }),
    ...(size === undefined ? {} : { size }),
    ...(sourceType === 'local' && own(record, 'customCover') !== undefined ? { customCover: assertPlaylistCover(own(record, 'customCover')) } : {}),
    ...(metadata ? { metadata } : {}),
    ...(lyrics?.length ? { lyrics } : {}),
    ...(amllLyrics?.length ? { amllLyrics } : {}),
    createdAt: normalizeDate(own(record, 'createdAt'), `${path}.createdAt`),
    updatedAt: normalizeDate(own(record, 'updatedAt'), `${path}.updatedAt`),
  };
  return track;
};

const normalizeLyricMap = <T>(value: unknown, path: string, ids: Set<string>, normalizer: (value: unknown, path: string) => T[] | undefined): Record<string, T[]> => {
  if (value === undefined || value === null) return {};
  const record = isRecord(value) ? value : fail('INVALID_SHAPE', `${path} 必须是对象`, path);
  const result: Record<string, T[]> = {};
  for (const key of Object.keys(record)) {
    if (DANGEROUS_KEYS.has(key)) continue;
    if (!ids.has(key)) fail('INVALID_REFERENCE', `${path}.${key} 未指向已导入歌曲`, `${path}.${key}`);
    const lines = normalizer(own(record, key), `${path}.${key}`);
    if (lines?.length) result[key] = lines;
  }
  return result;
};

const normalizePositions = (value: unknown, path: string, ids: Set<string>): Record<string, number> => {
  if (value === undefined || value === null) return {};
  const record = isRecord(value) ? value : fail('INVALID_SHAPE', `${path} 必须是对象`, path);
  const result: Record<string, number> = {};
  for (const key of Object.keys(record)) {
    if (DANGEROUS_KEYS.has(key)) continue;
    if (!ids.has(key)) fail('INVALID_REFERENCE', `${path}.${key} 未指向已导入歌曲`, `${path}.${key}`);
    result[key] = asNumber(own(record, key), `${path}.${key}`, { min: 0, max: MAX_POSITION_SECONDS });
  }
  return result;
};

const normalizeHistoryTimes = (value: unknown, history: string[]): Record<string, number> => {
  if (value === undefined || value === null) return {};
  const record = isRecord(value) ? value : fail('INVALID_SHAPE', 'historyPlayedAt 必须是对象', 'historyPlayedAt');
  const ids = new Set(history);
  const result: Record<string, number> = {};
  for (const id of Object.keys(record)) {
    if (DANGEROUS_KEYS.has(id)) continue;
    const path = `historyPlayedAt.${id}`;
    if (!ids.has(id)) fail('INVALID_REFERENCE', `${path} 未指向播放历史`, path);
    const time = asNumber(own(record, id), path, { min: 1, max: 8_640_000_000_000_000 });
    if (!Number.isSafeInteger(time)) fail('INVALID_VALUE', `${path} 必须是毫秒时间戳`, path);
    result[id] = time;
  }
  return result;
};

const normalizePlaylists = (value: unknown, ids: Set<string>): MetadataBackupPlaylist[] => {
  if (value === undefined || value === null) return [];
  const items: unknown[] = Array.isArray(value) ? value : fail('INVALID_SHAPE', 'playlists 必须是数组', 'playlists');
  if (items.length > MAX_PLAYLISTS) fail('LIMIT_EXCEEDED', '歌单数量过多', 'playlists');
  const seen = new Set<string>();
  const playlists = items.map((rawItem, index) => {
    const item = isRecord(rawItem) ? rawItem : fail('INVALID_SHAPE', `playlists[${index}] 必须是对象`, `playlists[${index}]`);
    const path = `playlists[${index}]`;
    const id = asId(own(item, 'id'), `${path}.id`);
    if (seen.has(id)) fail('DUPLICATE_ID', `歌单 ID 重复：${id}`, `${path}.id`);
    seen.add(id);
    const trackIds = normalizeTextList(own(item, 'trackIds'), `${path}.trackIds`, MAX_PLAYLIST_TRACKS);
    for (const trackId of trackIds) if (!ids.has(trackId)) fail('INVALID_REFERENCE', `${path}.trackIds 含未知歌曲 ${trackId}`, `${path}.trackIds`);
    let cover: { customCover?: string; coverUpdatedAt?: string };
    try { cover = playlistCoverFields({ customCover: own(item, 'customCover'), coverUpdatedAt: own(item, 'coverUpdatedAt') }); }
    catch (error) { return fail('INVALID_VALUE', (error as Error).message, `${path}.customCover`); }
    const description = asString(own(item, 'description'), `${path}.description`, false, 2000);
    const rawUpdatedAt = own(item, 'updatedAt');
    const updatedAt = rawUpdatedAt === null || rawUpdatedAt === '' ? undefined : rawUpdatedAt;
    return { id, name: asString(own(item, 'name'), `${path}.name`, true, 512)!, ...(description !== undefined ? { description } : {}), trackIds, createdAt: normalizeDate(own(item, 'createdAt'), `${path}.createdAt`), ...(updatedAt !== undefined ? { updatedAt: normalizeDate(updatedAt, `${path}.updatedAt`) } : {}), ...cover };
  });
  try { assertPlaylistCoverBudget(playlists); }
  catch (error) { fail('LIMIT_EXCEEDED', (error as Error).message, 'playlists'); }
  return playlists;
};

const normalizeBackup = (input: unknown): MetadataBackup => {
  const record = isRecord(input) ? input : fail('INVALID_SHAPE', '备份根节点必须是对象');
  const version = normalizeVersion(own(record, 'version'));
  const rawTracks = own(record, 'tracks');
  if (rawTracks !== undefined && !Array.isArray(rawTracks)) fail('INVALID_SHAPE', 'tracks 必须是数组', 'tracks');
  const trackValues = (rawTracks || []) as unknown[];
  if (trackValues.length > MAX_TRACKS) fail('LIMIT_EXCEEDED', '歌曲条目过多', 'tracks');
  const tracks = trackValues.map(normalizeTrack);
  const trackIds = new Set<string>();
  for (const track of tracks) {
    if (trackIds.has(track.id)) fail('DUPLICATE_ID', `歌曲 ID 重复：${track.id}`, 'tracks');
    trackIds.add(track.id);
  }

  const lyricsMap = normalizeLyricMap(own(record, 'lyrics'), 'lyrics', trackIds, normalizeLyricLines);
  const amllMap = normalizeLyricMap(own(record, 'amllLyrics'), 'amllLyrics', trackIds, normalizeAmllLines);
  for (const track of tracks) {
    if (!track.lyrics?.length && lyricsMap[track.id]) track.lyrics = lyricsMap[track.id];
    if (!track.amllLyrics?.length && amllMap[track.id]) track.amllLyrics = amllMap[track.id];
    if (track.lyrics?.length && !lyricsMap[track.id]) lyricsMap[track.id] = track.lyrics;
    if (track.amllLyrics?.length && !amllMap[track.id]) amllMap[track.id] = track.amllLyrics;
  }

  const queue = normalizeTextList(own(record, 'queue'), 'queue', MAX_QUEUE_ITEMS);
  const history = normalizeTextList(own(record, 'history'), 'history', MAX_HISTORY_ITEMS);
  const favorites = normalizeTextList(own(record, 'favorites'), 'favorites', MAX_FAVORITES);
  for (const [name, refs] of [['queue', queue], ['history', history], ['favorites', favorites]] as const) {
    for (const id of refs) if (!trackIds.has(id)) fail('INVALID_REFERENCE', `${name} 含未知歌曲 ${id}`, name);
  }
  const currentValue = own(record, 'currentId');
  const currentId = currentValue === undefined || currentValue === null ? null : asId(currentValue, 'currentId');
  if (currentId && !trackIds.has(currentId)) fail('INVALID_REFERENCE', `currentId 未指向已导入歌曲`, 'currentId');

  const rawTheme = own(record, 'theme');
  const theme: BackupTheme = rawTheme === undefined || rawTheme === null ? 'system' : rawTheme === 'light' || rawTheme === 'dark' || rawTheme === 'system' ? rawTheme : fail('INVALID_VALUE', 'theme 无效', 'theme');
  const rawMode = own(record, 'playMode');
  const playMode: BackupPlaybackMode = rawMode === undefined || rawMode === null ? 'sequence' : rawMode === 'sequence' || rawMode === 'repeat-all' || rawMode === 'repeat-one' || rawMode === 'shuffle' ? rawMode : fail('INVALID_VALUE', 'playMode 无效', 'playMode');
  const rawMotion = own(record, 'motionLevel') ?? own(record, 'background');
  const motionLevel: BackupMotionLevel = rawMotion === undefined || rawMotion === null ? 'medium' : rawMotion === 'high' || rawMotion === 'medium' || rawMotion === 'low' || rawMotion === 'off' ? rawMotion : fail('INVALID_VALUE', 'motionLevel 无效', 'motionLevel');
  const rawOffset = own(record, 'lyricOffsetMs');
  const lyricOffsetMs = rawOffset === undefined || rawOffset === null ? 0 : Math.max(-5_000, Math.min(5_000, Math.round(asNumber(rawOffset, 'lyricOffsetMs') / 100) * 100));
  const rawVolume = own(record, 'volume');
  const volume = rawVolume === undefined || rawVolume === null ? 0.8 : asNumber(rawVolume, 'volume', { min: 0, max: 1 });
  const exportedAtValue = own(record, 'exportedAt');
  const exportedAt = exportedAtValue === undefined || exportedAtValue === null ? undefined : normalizeDate(exportedAtValue, 'exportedAt');

  const result: MetadataBackup = {
    version,
    ...(exportedAt ? { exportedAt } : {}),
    tracks,
    queue,
    history,
    historyPlayedAt: normalizeHistoryTimes(own(record, 'historyPlayedAt'), history),
    favorites,
    playlists: normalizePlaylists(own(record, 'playlists'), trackIds),
    currentId,
    positions: normalizePositions(own(record, 'positions'), 'positions', trackIds),
    lyrics: lyricsMap,
    amllLyrics: amllMap,
    theme,
    playMode,
    lyricOffsetMs,
    motionLevel,
    volume,
  };
  return result;
};

/** Remove prototype-pollution keys before projecting the untrusted JSON object. */
const stripDangerousKeys = (value: unknown, depth = 0): unknown => {
  if (depth > MAX_DEPTH) fail('LIMIT_EXCEEDED', 'JSON 嵌套层级过深');
  if (Array.isArray(value)) {
    if (value.length > MAX_TRACKS * 2) fail('LIMIT_EXCEEDED', 'JSON 数组过大');
    return value.map((item) => stripDangerousKeys(item, depth + 1));
  }
  if (!isRecord(value)) return value;
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(value)) {
    if (DANGEROUS_KEYS.has(key)) continue;
    result[key] = stripDangerousKeys(value[key], depth + 1);
  }
  return result;
};

const assertPayloadSize = (text: string): void => {
  const bytes = new TextEncoder().encode(text).byteLength;
  if (bytes > MAX_BACKUP_BYTES) fail('LIMIT_EXCEEDED', `备份超过 ${MAX_BACKUP_BYTES} 字节上限`);
};

export function serializeBackup(saved: unknown): string {
  const normalized = normalizeBackup(saved);
  const text = JSON.stringify(normalized);
  assertPayloadSize(text);
  return text;
}

export function parseBackup(text: string): MetadataBackup {
  if (typeof text !== 'string') fail('INVALID_SHAPE', '备份内容必须是字符串');
  assertPayloadSize(text);
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    fail('INVALID_JSON', '备份不是有效 JSON');
  }
  return normalizeBackup(stripDangerousKeys(parsed));
}

